import { useCallback } from 'react';

import { truthyPredicate } from '@iconik-io/core/src/truthy_predicate';

import { getSearchSuggestions } from '../../../../ducks/search_suggest/thunks';
import { useAppDispatch } from '../../../../store/react_redux_typed';
import type { MultiSelectFilterFieldWrapperProps } from '../multi_select_filter_field/multi_select_filter_field_wrapper';
import { MultiSelectFilterFieldWrapper } from '../multi_select_filter_field/multi_select_filter_field_wrapper';
import type { MultiSelectFilterItemDisplay } from '../multi_select_filter_field/types';

type MultiSelectFilterTagCloudFieldProps = Pick<
  MultiSelectFilterFieldWrapperProps,
  'name' | 'value' | 'onChange' | 'label' | 'subtitle' | 'vertical' | 'disabled'
>;

export function MultiSelectFilterTagCloudField(
  props: MultiSelectFilterTagCloudFieldProps,
) {
  const dispatch = useAppDispatch();
  const fieldName = props.name.replace('filters.', '');

  const handleLoadOptions = useCallback(
    async (query: string): Promise<MultiSelectFilterItemDisplay[]> => {
      const trimmed = query.trim();
      if (!trimmed) {
        return [];
      }

      const response = await dispatch(
        getSearchSuggestions({ query: trimmed, field_name: fieldName }),
      );
      // A suggestion with no value has nothing to show or select.
      return response.body.objects
        .map((suggestion) => suggestion.value)
        .filter(truthyPredicate)
        .map((value) => ({ id: value, label: value }));
    },
    [fieldName],
  );

  const handleResolveSelectedItems = useCallback(
    async (ids: string[]): Promise<MultiSelectFilterItemDisplay[]> =>
      ids.map((id) => ({ id, label: id })),
    [],
  );

  return (
    <MultiSelectFilterFieldWrapper
      {...props}
      loadOptions={handleLoadOptions}
      resolveSelectedItems={handleResolveSelectedItems}
      itemLabels={{ singular: 'tag', plural: 'tags' }}
      showItemAvatar={false}
      showStartTypingHint={true}
      startTypingHint="Start typing to search"
    />
  );
}
